/**
 * ModernLayout Component
 * Modern column-based layout with fixed sections
 */

import React from 'react';
import { ThemeToggle } from '@/components/features/auth/ThemeToggle';
import { UserMenu } from './UserMenu';
import { MenuTree } from '@/components/features/menu/MenuTree';
import { MenuBreadcrumb } from '@/components/features/breadcrumb/MenuBreadcrumb';
import { FloatingNavBar } from './FloatingNavBar';
import { AIAssistantPanel } from '@/components/features/ai-assistant';
import {
  BlockstreamPanel,
  BlockstreamActivityPanel,
} from '@/components/features/blockstream';
import { ContactsPanel } from '@/components/features/contacts';
import { useFloatingMenu } from '@/hooks/useFloatingMenu';
import { useAuth } from '@/context/AuthContext';
import './ModernLayout.css';

interface ModernLayoutProps {
  children: React.ReactNode;
  noPadding?: boolean;
}

export const ModernLayout: React.FC<ModernLayoutProps> = ({
  children,
  noPadding = false,
}) => {
  const { user } = useAuth();

  // Use floating menu hook - returns menuItems array from API
  const { menuItems, toggle, getStatus, hasItem } = useFloatingMenu();

  // Get status for each panel from menuItems
  const sideMenuOpen = getStatus('SIDE_MENU');
  const aiChatOpen = getStatus('AI_CHAT');
  const blockstreamOpen = getStatus('BLOCKSTREAM');
  const calendarOpen = getStatus('MOJ_KALENDAR');
  const contactsOpen = getStatus('CONTACTS');

  return (
    <div className="modern-layout-container">
      {/* Top Navigation Bar */}
      <div className="modern-layout-navbar">
        <div className="modern-layout-navbar-left">
          <div className="modern-layout-logo">
            <span className="modern-layout-logo-text">
              {user?.tenant?.display_name || 'Thilo'}
            </span>
          </div>
        </div>
        <div className="modern-layout-navbar-center">
          <FloatingNavBar menuItems={menuItems} onToggle={toggle} />
        </div>
        <div className="modern-layout-navbar-right">
          <ThemeToggle />
          <UserMenu />
        </div>
      </div>

      {/* Main Content Area with Columns */}
      <div className="modern-layout-body">
        {/* Left Sidebar - Menu */}
        <div
          className={`modern-layout-sidebar modern-layout-sidebar-left ${sideMenuOpen ? 'modern-sidebar-open' : 'modern-sidebar-closed'}`}
        >
          <MenuTree />
        </div>

        {/* AI Chat Panel - Between Menu and Main */}
        {hasItem('AI_CHAT') && (
          <div
            className={`modern-layout-sidebar modern-layout-sidebar-ai ${aiChatOpen ? 'modern-sidebar-open' : 'modern-sidebar-closed'}`}
          >
            <AIAssistantPanel
              isOpen={true}
              onClose={() => toggle('AI_CHAT')}
              isInline={true}
            />
          </div>
        )}

        {/* Main Content Area - Always Visible */}
        <div className="modern-layout-main">
          {/* Breadcrumb */}
          <div className="modern-layout-breadcrumb">
            <MenuBreadcrumb />
          </div>

          {/* Content */}
          <div
            className={`modern-layout-content ${noPadding ? 'modern-layout-content-no-padding' : ''}`}
          >
            {children}
          </div>
        </div>

        {/* Blockstream Activity - Between Main and Calendar */}
        {hasItem('BLOCKSTREAM') && (
          <div
            className={`modern-layout-sidebar modern-layout-sidebar-blockstream ${blockstreamOpen ? 'modern-sidebar-open' : 'modern-sidebar-closed'}`}
          >
            <BlockstreamActivityPanel
              isOpen={true}
              onClose={() => toggle('BLOCKSTREAM')}
              isInline={true}
            />
          </div>
        )}

        {/* Right Sidebar - Calendar */}
        {hasItem('MOJ_KALENDAR') && (
          <div
            className={`modern-layout-sidebar modern-layout-sidebar-right ${calendarOpen ? 'modern-sidebar-open' : 'modern-sidebar-closed'}`}
          >
            <BlockstreamPanel
              isOpen={true}
              onClose={() => toggle('MOJ_KALENDAR')}
              isInline={true}
            />
          </div>
        )}

        {/* Contacts Sidebar - Far Right */}
        {hasItem('CONTACTS') && (
          <div
            className={`modern-layout-sidebar modern-layout-sidebar-contacts ${contactsOpen ? 'modern-sidebar-open' : 'modern-sidebar-closed'}`}
          >
            <ContactsPanel
              isOpen={true}
              onClose={() => toggle('CONTACTS')}
              isInline={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};
